// Google Maps Geocoding Service
// Uses the Google Maps JavaScript SDK (loaded via @react-google-maps/api)
// This avoids CORS issues that occur with direct REST API calls

// Wait for Google Maps to be loaded
const waitForGoogle = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
      return;
    }
    
    // Wait up to 10 seconds for Google Maps to load
    let attempts = 0;
    const maxAttempts = 100;
    const interval = setInterval(() => {
      attempts++;
      if (window.google && window.google.maps) {
        clearInterval(interval);
        resolve(window.google.maps);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        reject(new Error('Google Maps failed to load'));
      }
    }, 100);
  });
};

// Geocode an address to coordinates
export const geocodeAddress = async (address) => {
  try {
    const maps = await waitForGoogle();
    const geocoder = new maps.Geocoder();
    
    return new Promise((resolve) => {
      geocoder.geocode(
        { 
          address: address,
          region: 'il',
          language: 'he'
        },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const result = results[0];
            const location = result.geometry.location;
            console.log('📍 Geocoded:', address, '→', result.formatted_address, `(${location.lat()}, ${location.lng()})`);
            resolve({
              lat: location.lat(),
              lng: location.lng(),
              address: result.formatted_address
            });
          } else {
            console.warn('⚠️ No geocoding results for:', address, 'Status:', status);
            resolve(null);
          }
        }
      );
    });
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

// Reverse geocode coordinates to address
export const reverseGeocode = async (lat, lng) => {
  try {
    const maps = await waitForGoogle();
    const geocoder = new maps.Geocoder();
    
    return new Promise((resolve) => {
      geocoder.geocode(
        { 
          location: { lat, lng },
          language: 'he'
        },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve({
              address: results[0].formatted_address,
              lat,
              lng
            });
          } else {
            console.warn('⚠️ Reverse geocoding failed:', status);
            resolve(null);
          }
        }
      );
    });
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

// Search for addresses (autocomplete)
export const searchAddresses = async (query) => {
  try {
    if (!query || query.trim().length < 2) return [];
    
    const maps = await waitForGoogle();
    const autocompleteService = new maps.places.AutocompleteService();
    
    return new Promise((resolve) => {
      autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'il' },
          language: 'he'
        },
        (predictions, status) => {
          if (status === 'OK' && predictions && predictions.length > 0) {
            console.log(`Found ${predictions.length} suggestions for "${query}"`);
            resolve(predictions.map(prediction => ({
              address: prediction.description,
              description: prediction.description,
              place_id: prediction.place_id
            })));
          } else {
            if (status !== 'ZERO_RESULTS') {
              console.warn('Places Autocomplete status:', status);
            }
            resolve([]);
          }
        }
      );
    });
  } catch (error) {
    console.error('Address search error:', error);
    return [];
  }
};

// Get place details by place_id
export const getPlaceDetails = async (placeId) => {
  try {
    const maps = await waitForGoogle();
    
    // PlacesService requires a DOM element or map
    const div = document.createElement('div');
    const placesService = new maps.places.PlacesService(div);
    
    // Add timeout to prevent hanging
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        console.warn('Place details request timed out');
        resolve(null);
      }, 5000); // 5 second timeout
      
      placesService.getDetails(
        {
          placeId: placeId,
          fields: ['geometry', 'formatted_address', 'name'],
          language: 'he'
        },
        (place, status) => {
          clearTimeout(timeoutId);
          if (status === 'OK' && place && place.geometry) {
            const location = place.geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
              address: place.formatted_address || place.name
            });
          } else {
            console.warn('Place details failed:', status);
            resolve(null);
          }
        }
      );
    });
  } catch (error) {
    console.error('Place details error:', error);
    return null;
  }
};
